import context from '@truckdown/components-systems';
import { PromotionType } from '@truckdown/locations';
import { AddPromotionModel, IPromotionManager } from '@truckdown/promotions';
import { defineComponent, ref, Ref, computed, onMounted } from "vue";
import validationError from '../../Validation/Error/Error.vue';
import { PromotionValidator } from '../Validation/PromotionValidator';
import locationLookup from '../LocationLookup/LocationLookup.vue';
import { showMessage, isCsrOrAdmin } from '../../Utilities/utilities';

interface IData {
    locationList: Ref<ISelectItem[]>,
    isLoading: Ref<boolean>,
    showError: Ref<boolean>,
    promotionValidator: Ref<any>,
    promotionToAdd: Ref<AddPromotionModel>,
    showLocationInput: Ref<boolean>,
    expiryDateInput: Ref<string>,
    noExpiryDate: Ref<boolean>
};
interface ISelectItem {
    value: string,
    display: string,
    terms?: string
};

const getInitialData = function (props: any) {

    let promotionToAdd = {
        title: '',
        message: '',
        accountId: props.accountid,
        site: context.system,
        type: PromotionType.All,
        applyToAllLocations: true,
        locationIds: [],
        services: []
    } as AddPromotionModel;

    return {
        locationList: ref<ISelectItem[]>([]),
        isLoading: ref<boolean>(false),
        showError: ref<boolean>(false),
        promotionValidator: ref<any>(new PromotionValidator(promotionToAdd)),
        promotionToAdd: ref<AddPromotionModel>(promotionToAdd as AddPromotionModel),
        showLocationInput: ref<boolean>(true),
        expiryDateInput: ref<any>(''),
        noExpiryDate: ref<boolean>(true),
    } as IData;
}

const getComputed = function (props: any, data: IData) {
    return {
        selectedLocations: computed(() => {
            return data.locationList.value.map((val) => {
                return val.value;
            });
        })
    };
}

const promotionUpdatedEventName = 'promotion-update';
const promotionCancelEventName = 'cancel';

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        addPromotionCancelClick: function (): void {
            ctx.emit(promotionCancelEventName);
        },
        addPromotionSaveClick: async function (): Promise<void> {
            await savePromotion(data, props, ctx);
        },
        expiryChange: function () {
            if (data.expiryDateInput.value === undefined) {
                data.noExpiryDate.value = true;
                data.expiryDateInput.value = '';
                data.promotionToAdd.value.expiry = undefined;
            } else {
                data.noExpiryDate.value = false;
                data.promotionToAdd.value.expiry = new Date(data.expiryDateInput.value);
            }
        },
        noExpiryChange: function () {
            if (data.noExpiryDate.value == true) {
                data.expiryDateInput.value = '';
                data.promotionToAdd.value.expiry = undefined;
            }
        },
        selectLocation: function (location: ISelectItem) {
            data.locationList.value.push(location);
            data.promotionToAdd.value.locationIds.push(location.value);
        },
        removeLocation: function (location: ISelectItem) {
            let idx = data.locationList.value.indexOf(location);
            if (idx >= 0) {
                data.locationList.value.splice(idx, 1);
            }
            var idx2 = data.promotionToAdd.value.locationIds.indexOf(location.value);
            if (idx2 >= 0) {
                data.promotionToAdd.value.locationIds.splice(idx2, 1);
            }
        }
    }
}

const savePromotion = async function (data: IData, props: any, ctx: any): Promise<void> {

    if (!data.isLoading.value) {
        data.isLoading.value = true;
        try {
            if (isPromotionValid(data)) {
                let mgr = await context.getService<IPromotionManager>('IPromotionManager');

                if (data.noExpiryDate.value === false) {
                    data.promotionToAdd.value.expiry = new Date(data.expiryDateInput.value);
                } else {
                    data.promotionToAdd.value.expiry = undefined;
                }

                var response = await mgr.addPromotion(data.promotionToAdd.value);
                if (!response || !response.ok) {
                    console.log('Error saving promotion: ' + response.message);
                    showMessage("Error saving promotion", "danger", true, true);
                } else {
                    showMessage("Promotion was successfully created.", "success", true, true);
                    ctx.emit(promotionUpdatedEventName, response.data);
                }
            }
        } catch (err) {
            console.error(err);
        }
        finally {
            data.isLoading.value = false;
        }
    }
}

const isPromotionValid = function (data: IData): boolean {
    data.promotionValidator.value = new PromotionValidator(data.promotionToAdd.value);
    return data.promotionValidator.value.validate();
}

export default defineComponent({
    props: {
        accountid: String
    },
    components: {
        'error': validationError,
        'location-lookup': locationLookup
    },
    emits: [promotionUpdatedEventName, promotionCancelEventName],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computed = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        return {
            ...data,
            ...computed,
            ...methods,
            'PromotionType': PromotionType,
            'isCsrOrAdmin': isCsrOrAdmin
        };
    }
});